import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api-client';
import { formatDeliveryStatus, isBlockedDeliveryCode } from '@/lib/deliveryStatus';
import { serviceFileEligibility } from '@uniticket/shared';
import type { DmpAddressAttributes, DmpNeighbourNumber, DmpNeighboursResponse } from '@uniticket/shared';

// « Voisin » (demande du user, 25-09) : les adresses du MÊME CÔTÉ de la rue, sans refaire une recherche par numéro.
// Décisions du 25-09 :
//  · aucun choix pair / impair à l'écran — la parité vient du numéro consulté, et le titre la dit seulement ;
//  · une carte par NUMÉRO : un MDU y est résumé, la console le détaille quand on l'ouvre (tableau Bâtiment) ;
//  · les deux numéros EXISTANTS les plus proches de chaque côté, et un repère pour ceux qui manquent au DMP ;
//  · « Visible chez le partenaire » est le MÊME verdict que la fiche (serviceFileEligibility) : une carte ne peut
//    pas dire « visible » d'une adresse que la fiche, une fois ouverte, dirait invisible ;
//  · console DMP (page) uniquement — pas dans le tiroir ouvert depuis un ticket.
// Couleurs de statut : AUCUNE n'existait (le tableau Bâtiment écrit le statut en clair). Vert = HOMES_PASSED,
// bleu = HOMES_CONNECT, ambre = NOT_AVAILABLE (l'ambre « inactif » de la fenêtre de validation) ; le rouge reste
// celui d'un code de livraison bloquant, comme partout.

const STATUS_TONE: Record<string, string> = {
  HOMES_PASSED: 'bg-emerald-50 text-emerald-700',
  HOMES_CONNECT: 'bg-sky-50 text-sky-700',
  NOT_AVAILABLE: 'bg-amber-50 text-amber-800',
};
const statusTone = (s: string | null) => (s && STATUS_TONE[s]) || 'bg-slate-100 text-slate-600';

/** La définition du scope de l'avertissement N24 : dans une scope list (V15 ou V2) et jamais retirée. */
const inScope = (a: DmpAddressAttributes) => (a.scopelistV15 != null || a.scopelistV2 != null) && a.outOfScopeAt == null;

/** Les numéros de même parité absents du DMP entre deux cartes (5 puis 11 → 7 et 9). */
function missingBetween(from: number, to: number): number[] {
  const out: number[] = [];
  for (let n = from + 2; n < to; n += 2) out.push(n);
  return out;
}

export function NeighboursSection({ jvid, archived, onOpen, onClose }: {
  jvid: number;
  /** L'adresse consultée est archivée (B-6) : sa carte le dit, ses voisins restent consultables. */
  archived: boolean;
  onOpen: (jvid: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [data, setData] = useState<DmpNeighboursResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suit l'adresse consultée : ouvrir une carte recharge la console, et la série se recentre sur elle.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    apiFetch<{ data: DmpNeighboursResponse }>(`/dmp/address/${jvid}/neighbours`)
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => { if (alive) { setData(null); setError((err as Error)?.message || t('tickets:dmpLookup.error')); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jvid, t]);

  const title = data?.parity === 'even'
    ? t('tickets:dmpLookup.neighbours.titleEven')
    : data?.parity === 'odd'
      ? t('tickets:dmpLookup.neighbours.titleOdd')
      : t('tickets:dmpLookup.neighbours.title');

  return (
    <section
      aria-label={title}
      className="rounded-2xl border border-brand-rose/40 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-rose/10">
          <Home className="h-5 w-5 text-brand-rose" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-800">{title}</p>
          {data && (
            <p className="text-xs text-slate-500">
              {t('tickets:dmpLookup.neighbours.subtitle', {
                street: data.streetName, postal: data.postalCode, city: data.city, max: data.maxDistance,
              })}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          title={t('tickets:dmpLookup.neighbours.close')}
          aria-label={t('tickets:dmpLookup.neighbours.close')}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3">
        {loading && !data ? (
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('tickets:dmpLookup.neighbours.loading')}
          </p>
        ) : error ? (
          <p className="text-xs font-medium text-red-600" role="alert">{t('tickets:dmpLookup.neighbours.error', { message: error })}</p>
        ) : data && data.parity == null ? (
          <p className="text-xs text-slate-500">{t('tickets:dmpLookup.neighbours.noParity')}</p>
        ) : data ? (
          <>
            <div className={cn('flex items-stretch gap-2 overflow-x-auto pb-1', loading && 'opacity-60')}>
              {data.numbers.map((g, i) => {
                const prev = data.numbers[i - 1];
                const gap = prev && prev.numeric !== g.numeric ? missingBetween(prev.numeric, g.numeric) : [];
                return (
                  <div key={g.number} className="flex items-stretch gap-2">
                    {gap.length > 0 && <MissingMarker numbers={gap} />}
                    <NumberCard
                      group={g}
                      street={data.streetName}
                      place={`${data.postalCode} ${data.city}`}
                      archived={archived && g.isCurrent}
                      onOpen={onOpen}
                    />
                  </div>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10px] text-slate-400">
              {data.numbers.length <= 1
                ? t('tickets:dmpLookup.neighbours.none', { max: data.maxDistance })
                : t('tickets:dmpLookup.neighbours.openHint')}
            </p>
          </>
        ) : null}
      </div>
    </section>
  );
}

/** Un repère « numéro absent du DMP » entre deux cartes — sans lui, 5 · 11 se lirait comme deux maisons voisines. */
function MissingMarker({ numbers }: { numbers: number[] }) {
  const { t } = useTranslation(['tickets']);
  const label = numbers.length === 1
    ? t('tickets:dmpLookup.neighbours.missingOne', { n: numbers[0] })
    : numbers.length === 2
      ? t('tickets:dmpLookup.neighbours.missingTwo', { a: numbers[0], b: numbers[1] })
      : t('tickets:dmpLookup.neighbours.missingRange', { from: numbers[0], to: numbers[numbers.length - 1] });
  return (
    <div
      className="flex w-14 shrink-0 flex-col items-center justify-center gap-1 text-center text-[10px] leading-tight text-slate-400"
      title={t('tickets:dmpLookup.neighbours.missingHint')}
    >
      <span className="tracking-widest">⋯</span>
      <span>{label}</span>
      <span className="tracking-widest">⋯</span>
    </div>
  );
}

function NumberCard({ group, street, place, archived, onOpen }: {
  group: DmpNeighbourNumber;
  street: string;
  place: string;
  archived: boolean;
  onOpen: (jvid: number) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const units = group.units;
  const single = units.length === 1 ? units[0] : null;
  const types = [...new Set(units.map((u) => u.dwellingUnitType).filter(Boolean))].join(' / ') || '—';

  // Par statut et par code, dans l'ordre d'apparition : un MDU se lit « 2 HOMES_PASSED, 1 NOT_AVAILABLE ».
  const count = <K,>(keyOf: (a: DmpAddressAttributes) => K) => {
    const m = new Map<K, number>();
    for (const u of units) m.set(keyOf(u.attributes), (m.get(keyOf(u.attributes)) ?? 0) + 1);
    return [...m.entries()];
  };
  const statuses = count((a) => a.status);
  // Un logement SANS code au milieu d'autres qui en ont : le tiret n'apprend rien et se lisait comme un séparateur
  // (« — 7033 ×2 », vu à l'écran le 25-09). Il ne reste que si aucun logement n'a de code.
  const allCodes = count((a) => a.deliveryStatus);
  const codes = allCodes.some(([c]) => c != null) ? allCodes.filter(([c]) => c != null) : allCodes;
  const eligibility = units.map((u) => serviceFileEligibility(u.attributes));
  const visible = eligibility.filter((e) => e.eligible).length;
  const scoped = units.filter((u) => inScope(u.attributes)).length;

  const tally = (ok: number) => (ok === units.length ? 'text-emerald-700' : ok === 0 ? 'text-red-700' : 'text-amber-700');

  const visibleTitle = single && !eligibility[0].eligible
    ? t('tickets:dmpLookup.neighbours.blockedBy', {
      checks: eligibility[0].blocking.map((c) => t(`tickets:validation.serviceFile.checks.${c.id}`)).join(', '),
    })
    : undefined;
  const scopeTitle = single && !inScope(single.attributes)
    ? single.attributes.outOfScopeAt
      ? t('tickets:dmpLookup.neighbours.outSince', { date: single.attributes.outOfScopeAt })
      : t('tickets:dmpLookup.neighbours.noScopeList')
    : undefined;

  return (
    <button
      type="button"
      onClick={() => onOpen(units[0].jvid)}
      disabled={group.isCurrent}
      aria-label={t('tickets:dmpLookup.neighbours.openAria', { address: `${street} ${group.number}` })}
      className={cn(
        'flex w-52 shrink-0 flex-col rounded-xl border p-3 text-left transition-shadow',
        group.isCurrent
          ? 'border-brand-rose bg-rose-50/60 ring-1 ring-brand-rose/30'
          : 'border-slate-200 bg-white hover:border-brand-rose/40 hover:shadow-sm',
        // Hors scope = sortie du déploiement : la carte recule, sans disparaître de la série.
        !group.isCurrent && scoped === 0 && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'flex h-10 min-w-10 shrink-0 items-center justify-center rounded-lg px-2 text-lg font-bold',
            group.isCurrent ? 'bg-brand-rose/15 text-brand-rose' : 'bg-slate-100 text-slate-700',
          )}
        >
          {group.number}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-slate-800" title={`${street} ${group.number}`}>{street} {group.number}</p>
          <p className="truncate text-[11px] text-slate-500">{place}</p>
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
            <Home className="h-3 w-3" aria-hidden="true" />
            {types}
            {units.length > 1 && ` · ${t('tickets:dmpLookup.neighbours.units', { count: units.length })}`}
          </span>
        </div>
      </div>

      <dl className="mt-2.5 grid grid-cols-[auto_1fr] items-start gap-x-2 gap-y-1 border-t border-slate-100 pt-2 text-[11px]">
        <dt className="text-slate-400">JVID</dt>
        <dd className="font-mono text-slate-700">
          {single ? single.jvid : t('tickets:dmpLookup.neighbours.jvidCount', { count: units.length })}
        </dd>

        <dt className="text-slate-400">{t('tickets:dmpLookup.neighbours.status')}</dt>
        <dd className="flex flex-wrap gap-1">
          {statuses.map(([s, n]) => (
            <span key={s ?? '∅'} className={cn('rounded px-1.5 py-px text-[10px] font-semibold', statusTone(s))}>
              {units.length > 1 && `${n} `}{s ?? '—'}
            </span>
          ))}
        </dd>

        <dt className="text-slate-400">{t('tickets:dmpLookup.neighbours.delivery')}</dt>
        <dd className="flex flex-wrap gap-x-1.5">
          {codes.map(([code, n]) => (
            code == null ? <span key="∅" className="text-slate-400">—</span> : (
              <span
                key={code}
                title={formatDeliveryStatus(code)}
                className={cn(
                  'cursor-help underline decoration-dotted underline-offset-2',
                  isBlockedDeliveryCode(code) ? 'font-semibold text-red-700 decoration-red-300' : 'text-slate-700 decoration-slate-300',
                )}
              >
                {code}{units.length > 1 && n > 1 ? ` ×${n}` : ''}
              </span>
            )
          ))}
        </dd>

        <dt className="text-slate-400">{t('tickets:dmpLookup.neighbours.partner')}</dt>
        <dd className={cn('font-semibold', tally(visible))} title={visibleTitle}>
          {single
            ? (visible ? t('tickets:dmpLookup.neighbours.visible') : t('tickets:dmpLookup.neighbours.notVisible'))
            : t('tickets:dmpLookup.neighbours.visibleCount', { ok: visible, total: units.length })}
        </dd>

        <dt className="text-slate-400">{t('tickets:dmpLookup.neighbours.scope')}</dt>
        <dd className={cn('font-semibold', tally(scoped))} title={scopeTitle}>
          {single
            ? (scoped ? t('tickets:dmpLookup.neighbours.inScope') : t('tickets:dmpLookup.neighbours.outOfScope'))
            : t('tickets:dmpLookup.neighbours.inScopeCount', { ok: scoped, total: units.length })}
        </dd>
      </dl>

      {group.isCurrent && (
        <p className="mt-2 text-center text-[10px] font-bold uppercase tracking-wide text-brand-rose">
          {t('tickets:dmpLookup.neighbours.current')}
          {archived && ` · ${t('tickets:dmpLookup.neighbours.archived')}`}
        </p>
      )}
    </button>
  );
}
